import { useNavigate } from "@tanstack/react-router";
import { Plus, SearchLg, Star06, XClose } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { type AvailableProduct, useAvailableBriefProducts } from "@/api/brief-builder";
import { usePreviewSmartBrief } from "@/api/smart-briefs";
import { Button } from "@/components/base/buttons/button";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as showError } from "@/providers/ToastProvider";
import { BRIEF_MODAL_DIMENSIONS } from "@/utils/layout-constants";
import type { TheaterProps } from "../SmartBriefsLandingPage";
import { ProductTheaterView } from "./ProductTheaterView";
import { type DraftBriefData, SmartBriefEditModal } from "./SmartBriefEditModal";

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBriefCreated?: (briefId: string, productName: string) => void;
  navigateToEdit?: boolean;
  theaterProps?: TheaterProps;
  /** Pre-selected product ID from URL params */
  preselectedProductId?: string;
  /** Pre-selected product name from URL params */
  preselectedProductName?: string;
}

export const ProductSelectionModal = ({
  isOpen,
  onClose,
  onBriefCreated,
  navigateToEdit: _navigateToEdit = false,
  theaterProps: _theaterProps,
  preselectedProductId,
  preselectedProductName,
}: ProductSelectionModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(preselectedProductId || null);
  const [showTheater, setShowTheater] = useState(false);
  const [canCancelTheater, _setCanCancelTheater] = useState(true);
  const [showAddProductInput, setShowAddProductInput] = useState(false);
  const [customProductName, setCustomProductName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [draftData, setDraftData] = useState<DraftBriefData | null>(null);
  const navigate = useNavigate();
  const merchant = useMerchant();
  const cancelledRef = useRef(false);

  const { data: availableProducts, isLoading: isLoadingProducts } = useAvailableBriefProducts();
  const previewBrief = usePreviewSmartBrief();
  const hasAutoGenerated = useRef(false);

  // Use products from the database
  const products = availableProducts || [];

  // Filter products based on search and exclude "All Products"
  const filteredProducts = products
    .filter((product) => product.productName && product.productName !== "All Products")
    .filter((product) => product.productName.toLowerCase().includes(searchTerm.toLowerCase()));

  const productOptions = filteredProducts;

  // Get the selected product object
  const selectedProduct = products.find((p) => p.productId === selectedProductId);

  const handleProductSelect = (product: AvailableProduct) => {
    setSelectedProductId(product.productId);
    // Reset add product input when selecting a different product
    setShowAddProductInput(false);
    setCustomProductName("");
  };

  // Core brief generation logic that accepts a product name directly
  const handleGenerateBriefWithProductName = useCallback(
    async (productName: string) => {
      if (!productName) {
        showError("Please select a product.");
        return;
      }

      // Reset cancelled flag and show theater view
      cancelledRef.current = false;
      setShowTheater(true);

      const MINIMUM_THEATER_DURATION = 3500; // 3.5 seconds minimum

      try {
        // Run API call and minimum duration timer in parallel
        const [preview] = await Promise.all([
          previewBrief.mutateAsync({ productName }),
          new Promise((resolve) => setTimeout(resolve, MINIMUM_THEATER_DURATION)),
        ]);

        // Check if user cancelled while we were waiting
        if (cancelledRef.current) {
          return;
        }

        // Store draft data and show edit modal
        setDraftData({
          content: {
            ...preview.content,
            // Convert null to undefined for safetyList (API returns null, frontend expects undefined)
            safetyList: preview.content.safetyList ?? undefined,
          },
          examples: preview.examples || [],
          productName: preview.productName,
          productThumbnail: preview.productThumbnail ?? undefined,
        });

        // Hide theater, show edit modal
        setShowTheater(false);
        setShowEditModal(true);
      } catch (error) {
        // If cancelled, don't show error
        if (cancelledRef.current) {
          return;
        }
        // Hide theater on error
        setShowTheater(false);

        console.error("Error generating brief preview:", error);

        let errorMessage = "Failed to generate brief. ";

        if (error instanceof Error) {
          if (
            error.message.includes("BRIEF_DATA_NOT_FOUND") ||
            error.message.includes("Brief content data not found")
          ) {
            errorMessage =
              "Brief content data not available. The analysis data for this batch may not be ready yet. Please try again later or contact support if this persists.";
          } else {
            errorMessage += `Error: ${error.message}`;
          }
        } else {
          errorMessage += "Unknown error occurred.";
        }

        showError(errorMessage);
      }
    },
    [previewBrief],
  );

  // Reset hasAutoGenerated when modal closes or preselection changes
  useEffect(() => {
    if (!isOpen) {
      hasAutoGenerated.current = false;
    }
  }, [isOpen, preselectedProductId, preselectedProductName]);

  // Auto-generate brief when there's a preselected product
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isOpen && !isLoadingProducts && !hasAutoGenerated.current && (preselectedProductId || preselectedProductName)) {
      hasAutoGenerated.current = true;

      if (preselectedProductId) {
        const product = products.find((p) => p.productId === preselectedProductId);

        if (product) {
          setSelectedProductId(product.productId);

          timeoutId = setTimeout(() => {
            handleGenerateBriefWithProductName(product.productName);
          }, 100);

          return () => {
            if (timeoutId) clearTimeout(timeoutId);
          };
        }
      }

      if (preselectedProductName) {
        timeoutId = setTimeout(() => {
          handleGenerateBriefWithProductName(preselectedProductName);
        }, 100);
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    isLoadingProducts,
    preselectedProductId,
    preselectedProductName,
    products,
    handleGenerateBriefWithProductName,
  ]);

  const handleGenerateBrief = async () => {
    // Use custom product name if the input is shown and has a value, otherwise use selected product
    const productName =
      showAddProductInput && customProductName.trim() ? customProductName.trim() : selectedProduct?.productName;

    if (!productName) {
      showError("Please select a product.");
      return;
    }

    // If using custom product name, clear selection
    if (showAddProductInput && customProductName.trim()) {
      setSelectedProductId(null);
      setShowAddProductInput(false);
      setCustomProductName("");
    }

    await handleGenerateBriefWithProductName(productName);
  };

  const handleTheaterCancel = () => {
    cancelledRef.current = true;
    setShowTheater(false);
    onClose(); // Also close the product selection modal
  };

  const handleEditModalClose = () => {
    // Discard draft - just close everything
    setShowEditModal(false);
    setDraftData(null);
    onClose();
  };

  // Called when brief is successfully created from draft
  const handleBriefCreated = (briefId: string) => {
    setShowEditModal(false);
    setDraftData(null);
    onClose();

    // Call the callback if provided
    if (onBriefCreated && draftData) {
      onBriefCreated(briefId, draftData.productName);
    }

    // Navigate to the new brief's view page
    navigate({
      to: "/merchant/$merchantId/smart-briefs/brief/$briefId",
      params: { merchantId: merchant, briefId },
      search: { edit: false },
      replace: true,
    });
  };

  if (!isOpen && !showTheater && !showEditModal) return null;

  return (
    <>
      {/* Theater View */}
      <ProductTheaterView isOpen={showTheater} onClose={handleTheaterCancel} canCancel={canCancelTheater} />

      {/* Edit Modal - shown after brief generation (draft mode) */}
      <SmartBriefEditModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        draftData={draftData || undefined}
        onSaveComplete={handleBriefCreated}
      />

      {/* Full-screen modal overlay */}
      {isOpen && !showTheater && !showEditModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 overflow-hidden">
          {/* Large modal container - responsive with max dimensions */}
          <div
            className={`relative bg-white rounded-lg ${BRIEF_MODAL_DIMENSIONS.SIZE} p-6 inline-flex flex-col justify-start items-center gap-32 overflow-hidden`}
          >
            {/* Cancel button - top right (left-[1192px] in Figma = right-[100px] approximately) */}
            <Button
              color="secondary"
              size="sm"
              onClick={onClose}
              className="absolute right-20 top-[60px] z-10 gap-1"
              iconLeading={<XClose className="h-5 w-5 text-quaternary" />}
            >
              Cancel
            </Button>

            {/* Main content container */}
            <div className="w-[1280px] flex-1 max-w-[1280px] px-8 flex justify-center items-center">
              {/* Left side - Product selection (w-[446px], h-[635px] from Figma) */}
              <div className="w-[446px] h-[635px] flex flex-col gap-8">
                {/* Content area - title, search, and list */}
                <div className="flex-1 flex flex-col gap-5 min-h-0">
                  {/* Title */}
                  <div className="text-primary text-xl font-medium leading-8 shrink-0">
                    Select a product for this brief
                  </div>

                  {/* Search Input */}
                  <div className="shrink-0">
                    <div className="px-3.5 py-2.5 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-primary flex items-center gap-2 overflow-hidden">
                      <SearchLg className="w-5 h-5 text-quaternary shrink-0" />
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 bg-transparent text-base placeholder:text-placeholder focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Product List with scroll - takes remaining space */}
                  <div className="flex-1 min-h-0 relative">
                    {/* Scrollable product list */}
                    <div className="absolute inset-0 overflow-y-auto flex flex-col gap-2 pr-4">
                      {isLoadingProducts ? (
                        <div className="text-center py-12 text-quaternary">
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-solid border-t-transparent" />
                            Loading available products...
                          </div>
                        </div>
                      ) : (
                        <>
                          {productOptions.map((product) => (
                            <div
                              key={product.productId}
                              className={`pr-4 bg-white flex items-center gap-1 overflow-hidden cursor-pointer transition-all shrink-0 ${
                                selectedProductId === product.productId
                                  ? "rounded-md outline outline-2 outline-offset-[-2px] outline-brand"
                                  : "rounded-xl outline outline-1 outline-offset-[-1px] outline-secondary hover:outline-primary"
                              }`}
                              onClick={() => handleProductSelect(product)}
                            >
                              <div className="flex-1 flex items-center gap-4">
                                {/* Product thumbnail - 56x56 */}
                                <div className="w-14 h-14 rounded-lg bg-tertiary flex items-center justify-center overflow-hidden shrink-0">
                                  {product.thumbnail ? (
                                    <img
                                      src={product.thumbnail}
                                      alt={product.productName}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-sm text-quaternary font-medium">
                                      {product.productName.charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                </div>

                                {/* Product name */}
                                <span className="text-secondary text-sm font-medium leading-5">
                                  {product.productName}
                                </span>
                              </div>

                              {/* Checkbox */}
                              <CheckboxBase isSelected={selectedProductId === product.productId} size="sm" />
                            </div>
                          ))}

                          {/* Add product card - at bottom of list */}
                          {showAddProductInput ? (
                            // Add product input form
                            <div className="p-4 rounded-xl outline outline-2 outline-offset-[-2px] outline-brand flex flex-col gap-2 shrink-0 bg-white">
                              <div className="flex items-center justify-between">
                                <label className="text-secondary text-sm font-medium leading-5">
                                  Product name <span className="text-brand-solid">*</span>
                                </label>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setShowAddProductInput(false);
                                    setCustomProductName("");
                                  }}
                                  iconLeading={<XClose className="w-4 h-4 text-quaternary" />}
                                >
                                  Cancel
                                </Button>
                              </div>
                              <input
                                type="text"
                                placeholder="Enter product name"
                                value={customProductName}
                                onChange={(e) => setCustomProductName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && customProductName.trim()) {
                                    // Generate brief with custom product name
                                    handleGenerateBrief();
                                  }
                                }}
                                autoFocus
                                className="px-3.5 py-2.5 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-primary text-sm placeholder:text-placeholder focus:outline-brand focus:outline-2"
                              />
                            </div>
                          ) : (
                            // Info card with "Add product" button
                            <div className="bg-secondary p-4 rounded-xl flex items-center justify-between shrink-0">
                              <div className="flex flex-col gap-1">
                                <span className="text-secondary text-sm font-semibold leading-5">
                                  This list is synced from TikTok
                                </span>
                                <span className="text-tertiary text-sm font-normal leading-5">
                                  Don't see your product?
                                </span>
                              </div>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={() => {
                                  setShowAddProductInput(true);
                                  setSelectedProductId(null); // Deselect any selected product
                                }}
                                iconLeading={<Plus className="w-5 h-5" />}
                              >
                                Add product
                              </Button>
                            </div>
                          )}

                          {/* Spacer for gradient */}
                          <div className="h-14 shrink-0" />

                          {/* No products found message */}
                          {productOptions.length === 0 && searchTerm && (
                            <div className="text-center py-8 text-quaternary">
                              <p>No products found matching "{searchTerm}"</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Gradient fade at bottom */}
                    <div className="absolute bottom-0 left-0 right-4 h-14 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  size="lg"
                  onClick={handleGenerateBrief}
                  disabled={
                    (!selectedProductId && !(showAddProductInput && customProductName.trim())) || previewBrief.isPending
                  }
                  className="w-full shrink-0"
                  iconLeading={<Star06 className="w-5 h-5" />}
                >
                  {previewBrief.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating Preview...
                    </div>
                  ) : (
                    "Generate new Brief"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
